import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  SelectChangeEvent,
  FormControl,
  Autocomplete,
} from "@mui/material";
import { FileDownload, Print } from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DynamicTable from "../../../components/lab-dynamic-table";
import { formatCurrency } from "../../../utils/numberUtils";
import DateRangeSelector from "../../../components/DateRangeSelector";
import { useSelector } from "react-redux";
import { color } from "html2canvas/dist/types/css/types/color";

// Transaction data interface
interface TransactionData {
  _id: string;
  nowPaying: number;
  dueAmount: number;
  status: string;
  createdAt: string;
  collectionCenterName: string;
  mrn: number;
  patientName: string;
  phonNumber: string;
  trNo: number;
  paymentStatus: string;
  receivedBy: string;
  receivedOn?: string;
  createdBy?: string;
}

const Transaction: React.FC = () => {
  const today = new Date().toISOString().split("T")[0];

  const profile = useSelector((state: any) => state.profileSlice.profile);
  const collectionCenters = profile?.data?.collectionCenter || [];
  const labPermissions = profile?.data?.accessConfigurations?.Lab;
  const currentUserId = profile?.data?._id;
  const currentUserName = profile?.data?.fullName;

  const [transactionData, setTransactionData] = useState<TransactionData[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // State for date filtering
  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [users, setUsers] = useState<any[]>([]);
  const [userId, setUserId] = useState<string>("");
  const [selectedCollectionCenterId, setSelectedCollectionCenterId] = useState<string>("");
  const [selectedCollectionCenterIdNew, setSelectedCollectionCenterIdNew] = useState<string>("all");

  const handleCollectionCenterChange = (event: SelectChangeEvent) => {
    const selectedValue = event.target.value;
    setSelectedCollectionCenterIdNew(selectedValue);
    
    // If "all" is selected, clear the main dropdown
    if (selectedValue === "all") {
      setSelectedCollectionCenterId("");
    } else {
      // Sync with the main dropdown
      setSelectedCollectionCenterId(selectedValue);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/users/getHospitalAllUsers`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setUsers(response.data?.data || []);
    } catch (error: any) {
      console.error("Error fetching users:", error);
      toast.error(error?.response.data.message || "Error fetching users");
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Set current user automatically if can_view_all_users_reports is false
  useEffect(() => {
    if (
      labPermissions?.can_view_all_users_reports === false &&
      currentUserId &&
      !userId
    ) {
      setUserId(currentUserId);
    }
  }, [labPermissions?.can_view_all_users_reports, currentUserId, userId]);

  const fetchTransactionData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/dashboard/getAllLabTransaction`,
        {
          params: {
            count: rowsPerPage,
            pageNo: page + 1,
            fromDate,
            toDate,
            ...(selectedCollectionCenterIdNew !== "all" && { collectionCenterId: selectedCollectionCenterId }),
            ...(userId && { userId }),
          },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        setTransactionData(response.data.data || []);
        setTotalRecords(response.data.totalRecords || response.data.total || response.data.data?.length || 0);
      } else {
        toast.error(response.data.message || "Failed to fetch transactions");
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Error fetching transaction data"
      );
      setTransactionData([]);
    } finally {
      setLoading(false);
    }
  };

  // Refetch data when dependencies change
  useEffect(() => {
    if (selectedCollectionCenterIdNew === "all" || selectedCollectionCenterId) {
      fetchTransactionData();
    }
  }, [fromDate, toDate, page, rowsPerPage, selectedCollectionCenterId, selectedCollectionCenterIdNew, userId]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExcelExport = () => {
    if (!transactionData || transactionData.length === 0) {
      toast.warn("No data to export.");
      return;
    }

    const worksheetData = transactionData?.map(row => ({
      "TR No": row.trNo,
      MRN: row.mrn,
      "Patient Name": row.patientName,
      "Phone Number": row.phonNumber,
      "Collection Center": row.collectionCenterName,
      "Now Paying": row.nowPaying,
      // "Due Amount": row.dueAmount,
      Status: row.status,
      // "Payment Status": row.paymentStatus,
      "Received By": row.receivedBy || "-",
      "Received On": new Date(row.createdAt).toLocaleString(),
    }));

    const worksheet = xlsx.utils.json_to_sheet(worksheetData);
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "Transactions");
    xlsx.writeFile(
      workbook,
      `Lab_Transactions_${fromDate}_to_${toDate}.xlsx`
    );
  };

  const handlePrint = () => {
    if (!transactionData || transactionData.length === 0) {
      toast.warn("No data to print.");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 8;
    let yPosition = margin;

    // Professional header
    doc
      .setFontSize(10)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("LAB TRANSACTIONS REPORT", pageWidth / 2, yPosition, {
        align: "center",
      });

    yPosition += 3;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    // Metadata section
    yPosition += 3;
    doc
      .setFontSize(6.5)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Report Period: ${fromDate} to ${toDate} | Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 6;

    const head = [
      [
        "TR NO",
        "MRN",
        "PATIENT NAME",
        "PHONE",
        "CENTER",
        "PAYING",
        // "DUE",
        // "STATUS",
        "PAYMENT",
        "RECEIVED BY",
        "RECEIVED ON",
      ],
    ];

    const body = transactionData.map(row => [
      row.trNo?.toString() ?? "",
      row.mrn?.toString() ?? "",
      row.patientName ?? "",
      row.phonNumber ?? "",
      row.collectionCenterName ?? "",
      formatCurrency(row.nowPaying ?? 0),
      // formatCurrency(row.dueAmount ?? 0),
      row.status ?? "",
      // row.paymentStatus ?? "",
      row.receivedBy ?? "-",
      new Date(row.createdAt).toLocaleDateString(),
    ]);

    // Calculate totals
    const totals = transactionData.reduce(
      (acc, row) => ({
        nowPaying: acc.nowPaying + row.nowPaying,
        // dueAmount: acc.dueAmount + row.dueAmount,
      }),
      {
        nowPaying: 0,
        // dueAmount: 0,
      }
    );

    // Add totals row
    body.push([
      "TOTAL",
      "",
      "",
      "",
      "",
      formatCurrency(totals.nowPaying),
      // formatCurrency(totals.dueAmount),
      "",
      "",
      "",
      "",
    ]);

    // Clean, professional table styling
    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: {
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 6,
        halign: "center",
        valign: "middle",
        cellPadding: 2,
      },
      bodyStyles: {
        fontSize: 5.5,
        textColor: [40, 40, 40],
        cellPadding: 1.8,
      },
      columnStyles: {
        0: { halign: "center" },
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "center" },
        4: { halign: "center" },
        5: { halign: "center" },
        6: { halign: "center" },
        7: { halign: "center" },
        8: { halign: "center" },
        9: { halign: "center" },
        10: { halign: "center" },
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didParseCell: function (data) {
        // Professional total row with premium styling
        if (data.row.index === body.length - 1) {
          data.cell.styles.fillColor = [25, 55, 85];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fontSize = 5.8;
          data.cell.styles.lineColor = [200, 200, 200];
          data.cell.styles.lineWidth = 0.3;
          (data.cell.styles as any).cellPadding = {
            top: 2.5,
            right: 2,
            bottom: 2.5,
            left: 2,
          };
        }
      },
      didDrawPage: function (data) {
        // Add footer with page number
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        const pageNum = data.pageNumber;

        doc.setFontSize(5);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${pageNum} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      },
    });

    doc.autoPrint();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);
    
    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }
      window.removeEventListener("focus", cleanup);
    };
    
    window.addEventListener("focus", cleanup);
    iframe.src = blobUrl;
    
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Error triggering print dialog:", e);
        cleanup();
      }
    };
    
    // Fallback print trigger
    setTimeout(() => {
      try {
        const win = iframe.contentWindow;
        if (win && document.body.contains(iframe)) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Fallback print attempt failed:", e);
        cleanup();
      }
    }, 1000);
  };

  // Prepare headers for DynamicTable
  const headers = [
    "TR No",
    "MRN",
    "Patient Name",
    "Phone Number",
    "Collection Center",
    "Paid Amount",
    // "Due Amount",
    "Status",
    // "Payment Status",
    "Received By",
    "Received On",
  ];

  // Calculate totals for the table
  const tableTotals = transactionData.reduce(
    (acc, row) => ({
      nowPaying: acc.nowPaying + row.nowPaying,
      // dueAmount: acc.dueAmount + row.dueAmount,
    }),
    {
      nowPaying: 0,
      // dueAmount: 0,
    }
  );

  // Prepare data for DynamicTable (2D array format)
  const tableData = transactionData?.map(row => [
    row.trNo,
    row.mrn,
    row.patientName,
    row.phonNumber,
    row.collectionCenterName,
    formatCurrency(row.nowPaying),
    // formatCurrency(row.dueAmount),
    <Box
      component="span"
      sx={{
        color: row.status?.toLowerCase() === "in" ? "#16a34a" : row.status?.toLowerCase() === "out" ? "#dc2626" : "inherit",
        fontWeight: 600,
      }}
    >
      {row.status}
    </Box>,
    // row.paymentStatus,
    row.receivedBy || "-",
    new Date(row.createdAt).toLocaleString(),
  ]);

  // Calculate total amount for display
  const totalAmount = transactionData.reduce(
    (sum, item) => sum + item.nowPaying,
    0
  );

  // Add totals row to table data (only for display, not counted in pagination)
  if (transactionData.length > 0) {
    tableData.push([
      "TOTAL",
      "",
      "",
      "",
      "",
      formatCurrency(tableTotals.nowPaying),
      // formatCurrency(tableTotals.dueAmount),
      "",
      "",
      "",
      "",
    ]);
  }

  // Reusable button styles (matching payment summary)
 const gradientButtonStyle = {
    minWidth: { xs: "auto", md: "80px" },
    fontSize: { xs: "12px", md: "12px" },
    fontWeight: "600",
    textTransform: "none",
    borderRadius: "8px",
    background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
    color: "#ffffff",
    boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
      boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
      transform: "translateY(-1px)",
    },
  };

  return (
    <Box sx={{ background: "#fff", borderRadius: "12px" }}>
      <Box
        sx={{
          p: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownload />}
            onClick={handleExcelExport}
            sx={gradientButtonStyle}
            // disabled={loading || transactionData.length === 0}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={gradientButtonStyle}
            // disabled={loading || transactionData.length === 0}
          >
            Print
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Collection Center
            </Typography>
            <FormControl
              className="custom-input"
              size="small"
              fullWidth
              sx={{ minWidth: { xs: "100%", sm: "200px" } }}
            >
              <Select
                value={selectedCollectionCenterIdNew}
                onChange={handleCollectionCenterChange}
                displayEmpty
              >
                <MenuItem value="all">All</MenuItem>
                {collectionCenters.map((center: any) => (
                  <MenuItem key={center._id} value={center._id}>
                    {center.collectionCenterName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Created By
            </Typography>
            <Autocomplete
              size="small"
              fullWidth
              disabled={labPermissions?.can_view_all_users_reports === false}
              options={
                labPermissions?.can_view_all_users_reports === false
                  ? currentUserId
                    ? [{ _id: currentUserId, fullName: currentUserName }]
                    : []
                  : users
              }
              getOptionLabel={option => option.fullName || ""}
              value={
                labPermissions?.can_view_all_users_reports === false
                  ? currentUserId
                    ? { _id: currentUserId, fullName: currentUserName }
                    : null
                  : users?.find((doc: any) => doc._id === userId) || null
              }
              onChange={(event, newValue) => {
                if (labPermissions?.can_view_all_users_reports !== false) {
                  setUserId(newValue ? newValue._id : "");
                }
              }}
              sx={{
                minWidth: { xs: "100%", sm: "200px" },
                "& .MuiAutocomplete-input.Mui-disabled": {
                  color: "rgba(0, 0, 0, 0.6)",
                  WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
                },
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  className="custom-input"
                  placeholder={
                    labPermissions?.can_view_all_users_reports === false
                      ? "Current User Only"
                      : "Select user"
                  }
                />
              )}
            />
          </Box>
          <DateRangeSelector
            preset="lab"
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={(value) => {
              setDateRange(value);
              setPage(0);
            }}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>

      <Box
        sx={{
          p: 1,
          maxHeight: "60vh",
          overflowY: "hidden",
        }}
      >
        <DynamicTable
          headers={headers}
          data={tableData}
          loading={loading}
          height={"60vh"}
          totalAmount={totalAmount}
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalRecords > 0 ? totalRecords : transactionData.length}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};

export default Transaction;
